import axios from 'axios';

// Use the default axios instance which has interceptors configured
const api = axios;

export interface AISuggestions {
  threshold: number;
  generated_at: string;
  suggestions: string[];
  transaction_count: number;
  cache_expires_at: string;
}

export interface Budget {
  id: number;
  user_id: number;
  category: string;
  budget_amount: string | number;
  time_period: 'monthly' | 'yearly';
  current_spending: string | number; // Read-only, auto-calculated
  alert_thresholds: number[];
  createdAt: string;
  updatedAt: string;
  triggered_alerts?: number[];
  ai_suggestions?: AISuggestions; // AI-generated suggestions for budget optimization
}

export interface BudgetRemainderBreakdown {
  id: number;
  category: string;
  time_period: 'monthly' | 'yearly';
  budget_amount: number;
  monthly_equivalent: number;
}

export interface BudgetRemainder {
  monthly_salary: number;
  total_monthly_budget: number;
  remainder: number;
  budget_count: number;
  breakdown: BudgetRemainderBreakdown[];
}

export interface CreateBudgetRequest {
  category: string;
  budget_amount: number;
  time_period: 'monthly' | 'yearly';
  alert_thresholds?: number[];
}

export interface UpdateBudgetRequest {
  category?: string;
  budget_amount?: number;
  time_period?: 'monthly' | 'yearly';
  alert_thresholds?: number[];
}

export const budgetService = {
  // Get all budgets for the current user (auto-syncs spending from transactions)
  getBudgets: async (): Promise<Budget[]> => {
    const response = await api.get('/budgets');
    return response.data;
  },

  // Get a single budget by ID (auto-syncs spending)
  getBudgetById: async (id: number): Promise<Budget> => {
    const response = await api.get(`/budgets/${id}`);
    return response.data;
  },

  // Create a new budget
  createBudget: async (budget: CreateBudgetRequest): Promise<Budget> => {
    const response = await api.post('/budgets', budget);
    return response.data;
  },

  // Update a budget
  updateBudget: async (id: number, budget: UpdateBudgetRequest): Promise<Budget> => {
    const response = await api.patch(`/budgets/${id}`, budget);
    return response.data;
  },

  // Sync all budgets' spending from transactions
  syncAllBudgets: async (): Promise<{ message: string; updated: number }> => {
    const response = await api.post('/budgets/sync');
    return response.data;
  },

  // Sync a single budget's spending from transactions
  syncBudget: async (id: number): Promise<Budget> => {
    const response = await api.post(`/budgets/${id}/sync`);
    return response.data;
  },

  // Delete a budget
  deleteBudget: async (id: number): Promise<void> => {
    await api.delete(`/budgets/${id}`);
  },

  // Get monthly salary, total monthly budget, and remainder (projected monthly savings)
  getBudgetRemainder: async (): Promise<BudgetRemainder> => {
    const response = await api.get('/budgets/remainder');
    return response.data;
  },

  // Get budgets with AI suggestions
  getBudgetsWithAI: async (): Promise<Budget[]> => {
    console.log('Fetching budgets with AI from:', api.defaults.baseURL + '/budgets?include_ai=true');
    const response = await api.get('/budgets?include_ai=true');
    console.log('Response data:', response.data);
    return response.data;
  },

  // Get AI suggestions for a specific budget (new separated endpoint)
  getBudgetSuggestions: async (id: number): Promise<AISuggestions> => {
    console.log(`Fetching AI suggestions for budget ${id}`);
    const response = await api.get(`/budgets/${id}/suggestions`);
    console.log('AI Suggestions response:', response.data);
    // API returns { suggestions: {...} }, extract the suggestions object
    return response.data.suggestions || response.data;
  },

  // Get AI insight for a specific budget (legacy endpoint)
  getBudgetAIInsight: async (id: number): Promise<{ ai_suggestion: string }> => {
    const response = await api.get(`/budgets/${id}/ai-insight`);
    return response.data;
  },
};

export default budgetService;
